const STORAGE_KEY = 'mmo.keyBindings';

export const BINDABLE_ACTION_DEFINITIONS = [
  { id: 'moveNorth', labelKey: 'keyBindings.actions.moveNorth', defaultKey: 'w' },
  { id: 'moveSouthEast', labelKey: 'keyBindings.actions.moveSouthEast', defaultKey: 'd' },
  { id: 'moveSouth', labelKey: 'keyBindings.actions.moveSouth', defaultKey: 's' },
  { id: 'moveSouthWest', labelKey: 'keyBindings.actions.moveSouthWest', defaultKey: 'a' },
  { id: 'interact', labelKey: 'keyBindings.actions.interact', defaultKey: 'g' },
  { id: 'clearQueue', labelKey: 'keyBindings.actions.clearQueue', defaultKey: 'Control' },
  { id: 'useItem', labelKey: 'keyBindings.actions.useItem', defaultKey: 'f' },
  { id: 'toggleInventory', labelKey: 'keyBindings.actions.toggleInventory', defaultKey: 'i' },
  { id: 'toggleCharacter', labelKey: 'keyBindings.actions.toggleCharacter', defaultKey: 'j' },
  { id: 'toggleSkills', labelKey: 'keyBindings.actions.toggleSkills', defaultKey: 'k' },
  { id: 'toggleCodex', labelKey: 'keyBindings.actions.toggleCodex', defaultKey: 'c' },
  { id: 'toggleChat', labelKey: 'keyBindings.actions.toggleChat', defaultKey: 'x' },
  { id: 'toggleMinimap', labelKey: 'keyBindings.actions.toggleMinimap', defaultKey: 'm' },
  { id: 'toggleEquipment', labelKey: 'keyBindings.actions.toggleEquipment', defaultKey: 'u' },
  { id: 'toggleAdmin', labelKey: 'keyBindings.actions.toggleAdmin', defaultKey: 'o' },
  { id: 'toggleDebug', labelKey: 'keyBindings.actions.toggleDebug', defaultKey: 'y' },
  { id: 'toggleSocials', labelKey: 'keyBindings.actions.toggleSocials', defaultKey: 'r' },
  { id: 'toggleKeyBindings', labelKey: 'keyBindings.actions.toggleKeyBindings', defaultKey: 't' },
  { id: 'cameraPanUp', labelKey: 'keyBindings.actions.cameraPanUp', defaultKey: 'ArrowUp' },
  { id: 'cameraPanDown', labelKey: 'keyBindings.actions.cameraPanDown', defaultKey: 'ArrowDown' },
  { id: 'cameraPanLeft', labelKey: 'keyBindings.actions.cameraPanLeft', defaultKey: 'ArrowLeft' },
  { id: 'cameraPanRight', labelKey: 'keyBindings.actions.cameraPanRight', defaultKey: 'ArrowRight' },
  { id: 'cameraSnapToPlayer', labelKey: 'keyBindings.actions.cameraSnapToPlayer', defaultKey: ' ' },
] as const;

export type BindableAction = typeof BINDABLE_ACTION_DEFINITIONS[number]['id'];

export const BINDABLE_ACTIONS = BINDABLE_ACTION_DEFINITIONS.map(({ id }) => id) as BindableAction[];

export const DEFAULT_BINDINGS = BINDABLE_ACTION_DEFINITIONS.reduce((acc, def) => {
  acc[def.id] = def.defaultKey;
  return acc;
}, {} as Record<BindableAction, string>);

const ACTION_LABEL_KEYS = BINDABLE_ACTION_DEFINITIONS.reduce((acc, def) => {
  acc[def.id] = def.labelKey;
  return acc;
}, {} as Record<BindableAction, string>);

export function getActionLabelKey(action: BindableAction): string {
  return ACTION_LABEL_KEYS[action];
}

export function getDefaultBinding(action: BindableAction): string {
  return DEFAULT_BINDINGS[action];
}

function validateActionDefinitions(): void {
  const ids = new Set<string>();
  const defaults = new Set<string>();
  for (const def of BINDABLE_ACTION_DEFINITIONS) {
    const normalizedDefault = normalizeKey(def.defaultKey);
    if (ids.has(def.id)) {
      throw new Error(`[KeyBindings] Duplicate action id registration: ${def.id}`);
    }
    if (defaults.has(normalizedDefault)) {
      throw new Error(`[KeyBindings] Duplicate default key registration: ${normalizedDefault}`);
    }
    ids.add(def.id);
    defaults.add(normalizedDefault);
  }
}

export interface KeyBindingsManager {
  install(): void;
  dispose(): void;
  setEnabled(next: boolean): void;
  setStorageScope(scope: string): void;
  load(): void;
  save(): void;
  getKey(action: BindableAction): string;
  setBinding(action: BindableAction, key: string): void;
  getBindings(): Record<BindableAction, string>;
  resetAll(): void;
}

export function normalizeKey(raw: string): string {
  if (raw.length === 1) {
    return raw.toLowerCase();
  }
  return raw;
}

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el.isContentEditable;
}

function toEventTarget(target: EventTarget | null): EventTarget {
  if (target && typeof (target as EventTarget).dispatchEvent === 'function') {
    return target as EventTarget;
  }
  return window;
}

export function createKeyBindings(): KeyBindingsManager {
  validateActionDefinitions();

  let installed = false;
  let enabled = true;
  let storageScope: string | null = null;
  let bindings: Record<BindableAction, string> = { ...DEFAULT_BINDINGS };
  let reverseMap = new Map<string, string>();
  let orphanedKeys = new Set<string>();

  const getStorageKey = (): string => {
    if (!storageScope) return STORAGE_KEY;
    return `${STORAGE_KEY}.${encodeURIComponent(storageScope)}`;
  };

  const rebuildReverseMap = (): void => {
    const next = new Map<string, string>();
    const occupiedCanonicalKeys = new Set<string>();
    for (const action of BINDABLE_ACTIONS) {
      const userKey = normalizeKey(bindings[action]);
      const canonicalKey = normalizeKey(DEFAULT_BINDINGS[action]);
      occupiedCanonicalKeys.add(userKey);
      if (userKey !== canonicalKey) {
        next.set(userKey, canonicalKey);
      }
    }
    // Orphaned = canonical keys not occupied by any current binding
    const orphaned = new Set<string>();
    for (const action of BINDABLE_ACTIONS) {
      const canonicalKey = normalizeKey(DEFAULT_BINDINGS[action]);
      if (!occupiedCanonicalKeys.has(canonicalKey)) {
        orphaned.add(canonicalKey);
      }
    }
    reverseMap = next;
    orphanedKeys = orphaned;
  };

  const load = (): void => {
    bindings = { ...DEFAULT_BINDINGS };
    try {
      const raw = localStorage.getItem(getStorageKey());
      if (!raw) {
        rebuildReverseMap();
        return;
      }

      const parsed = JSON.parse(raw) as Partial<Record<BindableAction, unknown>>;
      for (const action of BINDABLE_ACTIONS) {
        const value = parsed[action];
        if (typeof value === 'string' && value.length > 0) {
          bindings[action] = normalizeKey(value);
        }
      }
    } catch {
      bindings = { ...DEFAULT_BINDINGS };
    }

    rebuildReverseMap();
  };

  const save = (): void => {
    localStorage.setItem(getStorageKey(), JSON.stringify(bindings));
  };

  const setStorageScope = (scope: string): void => {
    const trimmed = scope.trim();
    storageScope = trimmed.length > 0 ? trimmed : null;
    load();
  };

  const activeTranslatedKeys = new Set<string>();

  const translateEvent = (event: KeyboardEvent, type: 'keydown' | 'keyup'): void => {
    if (!enabled) {
      return;
    }

    if ((event as KeyboardEvent & { __mmoKeyTranslated?: boolean }).__mmoKeyTranslated) {
      return;
    }

    if (isEditableTarget(event.target)) {
      return;
    }

    const sourceKey = normalizeKey(event.key);

    // Suppress orphaned canonical keys (rebound away, slot now empty)
    if (orphanedKeys.has(sourceKey)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const canonicalKey = reverseMap.get(sourceKey);
    if (!canonicalKey || canonicalKey === sourceKey) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (type === 'keydown') {
      activeTranslatedKeys.add(canonicalKey);
    } else {
      activeTranslatedKeys.delete(canonicalKey);
    }

    const translated = new KeyboardEvent(type, {
      key: canonicalKey,
      code: event.code,
      location: event.location,
      repeat: event.repeat,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      bubbles: true,
      cancelable: true,
      composed: true,
    });

    (translated as KeyboardEvent & { __mmoKeyTranslated?: boolean }).__mmoKeyTranslated = true;
    toEventTarget(event.target).dispatchEvent(translated);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    translateEvent(event, 'keydown');
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    translateEvent(event, 'keyup');
  };

  const install = (): void => {
    if (installed) return;
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    installed = true;
  };

  const dispose = (): void => {
    if (!installed) return;
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('keyup', onKeyUp, true);
    installed = false;
  };

  const setEnabled = (next: boolean): void => {
    if (!next && enabled) {
      // Flush held translated keys so controllers see keyup
      for (const canonicalKey of activeTranslatedKeys) {
        window.dispatchEvent(new KeyboardEvent('keyup', {
          key: canonicalKey, bubbles: true, cancelable: true,
        }));
      }
      activeTranslatedKeys.clear();
    }
    enabled = next;
  };

  const getKey = (action: BindableAction): string => {
    return bindings[action];
  };

  const setBinding = (action: BindableAction, key: string): void => {
    const normalizedKey = normalizeKey(key);
    if (!normalizedKey) return;
    bindings[action] = normalizedKey;
    rebuildReverseMap();
  };

  const getBindings = (): Record<BindableAction, string> => {
    return { ...bindings };
  };

  const resetAll = (): void => {
    bindings = { ...DEFAULT_BINDINGS };
    rebuildReverseMap();
  };

  load();

  return {
    install,
    dispose,
    setEnabled,
    setStorageScope,
    load,
    save,
    getKey,
    setBinding,
    getBindings,
    resetAll,
  };
}
